import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { DeleteListItemRequest, ListModel } from "../../../../models/lists/list.models";
import { NotificationType, NotificationClientService } from "../../../../services/basics/notification-client.service";
import { ListService } from "../../../../services/modules/list-client.service";
import { RouterEvent } from "aurelia-router";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { computedFrom } from "aurelia-binding";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { DataGridColumn, DataType } from "../../../../models/queries/queries.models";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { ValidationResult } from "../../../../models/validations/validations.models";
import {BindingEngine} from "aurelia-framework";
import { SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";

export abstract class BaseListItems extends CustomGridPageBase {

    public businessUnitId: number = null;
    public customerId: number = null;
    public isRightShipUser: boolean = false;
    public canEditLists: boolean = false;
    public canEditProtectedList: boolean = false;
    public canEditProtectedListItem: boolean = false;
    public canEditListItems: boolean = false;
    public addSelectColumn: boolean = false;

    constructor(protected dialogClient: DialogClientService,
        protected notificationClientService: NotificationClientService,
        protected listService: ListService,
        i18N: I18N,
        protected bindingEngine: BindingEngine,
        itemsName: string,
        private activeMenuItemsService: ActiveMenuItemsService,
        private eventAggregator: EventAggregator,
        private customerService: CommonCustomerService,
        userClaimsService: UserClaimsService,
        private signalClientService: SignalClientService,
        private validationsClientService: ValidationsClientService) {
        super(i18N, itemsName, bindingEngine);
        this.customerService.getBusinessUnitId().then(id => {
            this.businessUnitId = id;
        });
        this.customerService.getCustomerId().then(id => {
            this.customerId = id;
        });

        this.isRightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
        this.canEditLists = userClaimsService.hasPermission(Claims.CanEditLists);
        this.canEditProtectedList = userClaimsService.hasPermission(Claims.CanEditProtectedList);
        this.canEditProtectedListItem = userClaimsService.hasPermission(Claims.CanEditProtectedListItem);
        this.canEditListItems = userClaimsService.hasPermission(Claims.CanEditListItems);
    }

    private _list: ListModel;

    public get list(): ListModel {
        return this._list;
    }

    public set list(x: ListModel) {
        this._list = x;
        this.listChanged();
    }

    @computedFrom('list.listId')
    public get listId(): number {
        return this.list ? this.list.listId : -1;
    }

    public activate(model: any) {
        this.list = typeof (model) == "string" ? this.parse(<string>model) : model;
        this.onActivated();
    }

    
    attached() {
        this.signalClientService.unsubscribe(SignalFunctions.listItemsDeleted, this.onListItemsDeletedSuccesfully);
        this.signalClientService.subscribe(SignalFunctions.listItemsDeleted, this.onListItemsDeletedSuccesfully);
    }

    protected onActivated() { }

    private parse(obj: string): ListModel {
        return JSON.parse(obj.replace(new RegExp("&quot;", "g"), "\"")
            .replace(new RegExp("&gt;", "g"), ">")
            .replace(new RegExp("&lt;", "g"), "<"));
    }

    @computedFrom("isRightShipUser", "list", "list.businessUnitId", "businessUnitId", "list.customerId", "customerId")
    public get showDeleteButton() {
        return this.canEditListItems && 
        (this.isRightShipUser || (this.list.businessUnitId && this.list.businessUnitId == this.businessUnitId)
            || (this.list.customerId && this.list.customerId == this.customerId)) &&
            (!this.list.isInternal || this.canEditProtectedListItem);
    }

    @computedFrom("anySelected", "canUpdateLists")
    public get disableDeleteButton() {
        return !this.anySelected || !this.canEditLists;
    }

    showDeleteDialog() {
        if ((!this.dataGridVM.toggleAll && this.dataGridVM.getSpecificIds().length == 0)) {
            this.notificationClientService.show(this.i18N.tr('no-items-selected-text'), "", NotificationType.Warning, 5000);
            return;
        }

        this.dialogClient.show(RightShipDialogServiceConstants.URLs.ListItemDeleteDialog, { deleteFunc: this.deleteListItems }, true, false, false, 'right');
    }

    get anySelected() {
        return this.dataGridVM && (this.dataGridVM.toggleAll || this.dataGridVM.getSpecificIds().length > 0)
    }

    onListItemsDeletedSuccesfully = () => {
        this.notificationClientService.show(this.i18N.tr('succesfully-deleted-list-items'), "", NotificationType.Info, 5000);
        if (this.dataGridVM) {
            this.dataGridVM.reloadData(null, null);
            this.dataGridVM.unselectAll();
        }
    }

    deleteListItems = (reason: string) => {
        return new Promise<void>((resolve, reject) => {
            const request: DeleteListItemRequest = {
                allSelected: this.dataGridVM.toggleAll,
                filters: this.dataGridVM.allFilters,
                ids: this.dataGridVM.getSpecificIds(),
                listId: this.listId,
                reason: reason
            }
            this.listService.deleteListItem(request).then(response => {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    reject();
                } else {
                    this.notificationClientService.show(this.i18N.tr('list-items-in-the-background'), "", NotificationType.Info, 5000);
                    this.dataGridVM.reloadData(null, null);
                    this.dataGridVM.unselectAll();
                    resolve();
                }
            }).catch(x => {
                const response: {
                    exception: string;
                    details: string;
                    validationResult: ValidationResult
                } = typeof (x.response) === 'object'
                        ? x.response
                        : (x.response ? JSON.parse(x.response) : {});
                if (!response.exception || !response.details) {
                    this.notificationClientService.show(this.i18N.tr('failed-list-item-deletion-text'), "", NotificationType.Error, 5000);
                } else if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                }
                reject();
            });
        })
    }

    setListsActive() {
        this.eventAggregator.subscribeOnce(RouterEvent.Complete, () => {
            this.activeMenuItemsService.setActiveFromMenuItem('Lists');
        });
    }

    listChanged() {

    }

    private _columns: DataGridColumn[] = [];

    get columns(): DataGridColumn[] {
        return this._columns;
    }

    set columns(cols: DataGridColumn[]) {
        if (this.addSelectColumn) {
            const selectColumn = <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: `<check-box checked.bind="getRowItem().isSelected"
                            changed.delegate="getDataGrid().selectRowInMultipleSelection(getRowItem())">
                        </check-box>`,
                name: "select",
                title: MobileHelper.isMobileDevice() ? "" : `<check-box class="check-box-select-all" checked.two-way="toggleAll"></check-box>`,
                parameter: {
                    item: null
                },
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -2,
                width: MobileHelper.isMobileDevice() ? "" : "38px",
                isHidden: false,
                isSortable: false,
                getCellClass: () => {
                    return "selector-checkbox";
                }
            };
            cols.unshift(selectColumn);
        }
        this._columns = cols;
    }
}
